import React, { useEffect, useState, useCallback } from "react";
import { listStudentAllAssignments, stopAssignment } from "../../services/authService";

// Panneau "Ses programmes" du volet éditeur.
// Deux sections : en cours (avec barre de progression + bouton Arrêter) et
// historique (statuts arrete / termine / a_venir avec compteur séances).
//
// Le calcul de progression temporelle est fait ici plutôt que côté back parce
// qu'il évolue en continu (l'utilisateur peut ouvrir la vue à tout moment).

const DAY_MS = 86400000;

const fmtDate = (ts) => {
	if (!ts) return "—";
	return new Date(Number(ts)).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

// Semaine 1..N basée sur la date de démarrage.
function currentWeek(dateDebut, dureeSemaines) {
	const now = Date.now();
	if (now < dateDebut) return 0;
	const daysIn = Math.floor((now - dateDebut) / DAY_MS);
	return Math.min(dureeSemaines, Math.floor(daysIn / 7) + 1);
}

function progressionPct(dateDebut, dateFin) {
	const now = Date.now();
	if (now <= dateDebut) return 0;
	if (now >= dateFin) return 100;
	return Math.round(((now - dateDebut) / (dateFin - dateDebut)) * 100);
}

function StatutBadge({ statut, dateArret }) {
	if (statut === "en_cours") return <span className="sp-badge sp-badge-active">En cours</span>;
	if (statut === "a_venir") return <span className="sp-badge sp-badge-upcoming">À venir</span>;
	if (statut === "arrete") return <span className="sp-badge sp-badge-stopped">Arrêté le {fmtDate(dateArret)}</span>;
	return <span className="sp-badge sp-badge-done">Terminé</span>;
}

export default function StudentProgramsPanel({ student, onNotif, onAfterStop }) {
	const [assignments, setAssignments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [stoppingId, setStoppingId] = useState(null);

	const refresh = useCallback(async () => {
		if (!student?.id) return;
		setLoading(true);
		const res = await listStudentAllAssignments(student.id);
		setLoading(false);
		if (res.success) setAssignments(res.assignments);
	}, [student?.id]);

	useEffect(() => { refresh(); }, [refresh]);

	const handleStop = async (a) => {
		// Prévisualisation : on estime les séances futures à supprimer via seancesTotales -
		// seancesPassees (le back renvoie déjà ces compteurs, on n'a pas besoin d'un
		// round-trip supplémentaire pour "preview").
		const futureCount = Math.max(0, (a.seancesTotales || 0) - (a.seancesPassees || 0));
		const confirmMsg = futureCount > 0
			? `Arrêter « ${a.programmeNom} » supprimera ${futureCount} séance${futureCount > 1 ? "s" : ""} future${futureCount > 1 ? "s" : ""}. Les séances déjà passées sont conservées.\n\nContinuer ?`
			: `Arrêter « ${a.programmeNom} » ? Aucune séance future ne sera supprimée (elles sont toutes déjà passées).`;
		if (!window.confirm(confirmMsg)) return;

		setStoppingId(a.idAssignation);
		const res = await stopAssignment(a.idAssignation);
		setStoppingId(null);
		if (res.success) {
			onNotif?.(
				res.deleted > 0
					? `Programme arrêté · ${res.deleted} séance${res.deleted > 1 ? "s" : ""} supprimée${res.deleted > 1 ? "s" : ""}`
					: "Programme arrêté"
			);
			refresh();
			onAfterStop?.();  // parent peut recharger la vue calendrier notamment
		} else {
			onNotif?.("Erreur lors de l'arrêt du programme", "error");
		}
	};

	if (loading) return <div className="sp-empty">Chargement…</div>;

	const enCours = assignments.filter((a) => a.statut === "en_cours" || a.statut === "a_venir");
	const historique = assignments.filter((a) => a.statut === "arrete" || a.statut === "termine");

	if (assignments.length === 0) {
		return (
			<div className="sp-empty">
				<div className="sp-empty-title">Aucun programme</div>
				<div className="sp-empty-hint">
					{student?.prenom || "L'élève"} n'a pas encore de programme assigné.
					Va dans l'onglet Programmes de la sidebar pour en créer un et l'assigner.
				</div>
			</div>
		);
	}

	return (
		<div className="sp-panel">
			{enCours.length > 0 && (
				<div className="sp-section">
					<h4 className="sp-section-title">En cours</h4>
					<div className="sp-list">
						{enCours.map((a) => {
							const week = currentWeek(a.dateDebut, a.dureeSemaines);
							const pct = progressionPct(a.dateDebut, a.dateFin);
							const isStopping = stoppingId === a.idAssignation;
							return (
								<div key={a.idAssignation} className="sp-card sp-card-active">
									<div className="sp-card-head">
										<div className="sp-card-title-wrap">
											<div className="sp-card-title">{a.programmeNom}</div>
											<StatutBadge statut={a.statut} dateArret={a.dateArret} />
										</div>
										<button
											type="button"
											className="sp-stop-btn"
											onClick={() => handleStop(a)}
											disabled={isStopping}
										>
											{isStopping ? "Arrêt…" : "Arrêter"}
										</button>
									</div>
									<div className="sp-card-meta">
										{a.statut === "a_venir"
											? <>Démarre le <strong>{fmtDate(a.dateDebut)}</strong></>
											: <>Semaine <strong>{week}</strong> / {a.dureeSemaines} · {pct}%</>
										}
									</div>
									<div className="sp-progress" aria-hidden="true">
										<div className="sp-progress-fill" style={{ width: `${pct}%` }} />
									</div>
									<div className="sp-card-dates">
										<span>{fmtDate(a.dateDebut)}</span>
										<span>→</span>
										<span>{fmtDate(a.dateFin)}</span>
										<span className="sp-card-dates-sep">·</span>
										<span>{a.seancesPassees}/{a.seancesTotales} séance{a.seancesTotales > 1 ? "s" : ""}</span>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{historique.length > 0 && (
				<div className="sp-section">
					<h4 className="sp-section-title">Historique</h4>
					<div className="sp-list">
						{historique.map((a) => (
							<div key={a.idAssignation} className="sp-card sp-card-past">
								<div className="sp-card-head">
									<div className="sp-card-title-wrap">
										<div className="sp-card-title">{a.programmeNom}</div>
										<StatutBadge statut={a.statut} dateArret={a.dateArret} />
									</div>
								</div>
								{/* Un programme arrêté affiche sa date d'arrêt en guise de fin. Arrêté
								    AVANT d'avoir démarré, cette date précède le début et la carte montrait
								    une plage inversée (« 7 sept. → 3 sept. »). La plage n'a alors aucun
								    sens : on annonce l'arrêt anticipé. */}
								{a.statut === "arrete" && a.dateArret != null && a.dateArret < a.dateDebut ? (
									<div className="sp-card-dates">
										<span>Arrêté le {fmtDate(a.dateArret)}, avant son démarrage prévu le {fmtDate(a.dateDebut)}</span>
									</div>
								) : (
									<div className="sp-card-dates">
										<span>{fmtDate(a.dateDebut)}</span>
										<span>→</span>
										<span>{fmtDate(a.statut === "arrete" ? a.dateArret : a.dateFin)}</span>
										<span className="sp-card-dates-sep">·</span>
										<span>{a.seancesPassees}/{a.seancesTotales} séance{a.seancesTotales > 1 ? "s" : ""}</span>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
